// File: src/components/Kiln/KilnPlanner.jsx
// Kiln planner UI with Mudo card styling.

import React, { useMemo, useState } from "react";
import { apiFetch } from "../../lib/apiFetch";
import { useKilns } from "../../contexts/KilnContext";
import { estimateKilnLoad } from "../../ai/estimators/kilnCapacityEstimator";
import { usePricingSettings } from "../../contexts/PricingSettingsContext";

export default function KilnPlanner() {
  const { kilns, selectedKiln, selectedKilnId, setSelectedKilnId } = useKilns();
  const [aiCoach, setAiCoach] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const { pricingSettings } = usePricingSettings();

  // TODO: Replace placeholder with real data from Planner/Calculator.
  const [availableBatches] = useState(() => [
    {
      id: "batch-mugs-1",
      name: "Mugs – Batch 1",
      productName: "Standard Mug",
      quantity: 200,
      spaceUnitsPerPiece: 1,
      totalSpaceUnits: 200,
      dueDate: null,
    },
    {
      id: "batch-bowls-1",
      name: "Bowls – Batch 1",
      productName: "Soup Bowl",
      quantity: 40,
      spaceUnitsPerPiece: 1.2,
      totalSpaceUnits: 48,
      dueDate: null,
    },
  ]);

  const [loadBatchIds, setLoadBatchIds] = useState(() => []);

  const loadItems = useMemo(
    () =>
      availableBatches
        .filter((b) => loadBatchIds.includes(b.id))
        .map((b) => ({
          id: b.id,
          name: b.name,
          quantity: b.quantity,
          spaceUnitsPerPiece: b.spaceUnitsPerPiece,
          totalSpaceUnits: b.totalSpaceUnits,
          dueDate: b.dueDate,
        })),
    [availableBatches, loadBatchIds]
  );

  const loadInfo = useMemo(
    () => estimateKilnLoad(selectedKiln, loadItems),
    [selectedKiln, loadItems]
  );

  function toggleBatch(batchId) {
    setLoadBatchIds((prev) =>
      prev.includes(batchId)
        ? prev.filter((id) => id !== batchId)
        : [...prev, batchId]
    );
  }

  function handleSplitFiring() {
    // TODO: Implement real split logic using capacity & due dates.
    alert(
      "Split firing: TODO – this will suggest two firings based on kiln capacity."
    );
  }

  function handleSuggestTopUp() {
    // TODO: Implement real top-up suggestion logic by scanning available batches.
    alert(
      "Top-up suggestion: TODO – this will suggest compatible batches to fill the kiln."
    );
  }

  const percentFull = Math.min(
    100,
    Math.max(0, Math.round((loadInfo.loadRatio || 0) * 100))
  );

  async function handleAskKilnAi() {
    if (!selectedKiln || loadItems.length === 0 || pricingSettings?.enableKilnAi === false) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const context = {
        kilnName: selectedKiln.name,
        capacityUnits: loadInfo.capacity,
        totalUnits: loadInfo.totalSpaceUnits,
        loadRatio: loadInfo.loadRatio,
        percentFull,
        batches: loadItems.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          totalSpaceUnits: item.totalSpaceUnits,
        })),
      };
      const res = await apiFetch("/api/ai/coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "kiln", context }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "AI error");
      setAiCoach(json.result);
    } catch (err) {
      console.error("[KilnPlanner] AI error", err);
      setAiError("Mudo AI couldn’t review this firing right now.");
    } finally {
      setAiLoading(false);
    }
  }
  const capacityBg = (() => {
    const ratio = loadInfo.loadRatio || 0;
    if (ratio > 1.05) return "#b91c1c";
    if (ratio > 0.9) return "#15803d";
    if (ratio > 0.7) return "#a16207";
    return "#6b7280";
  })();

  return (
    <div className="p-6 space-y-6 overflow-auto">
      <div>
        <h1 className="text-xl font-semibold">Kiln Planner</h1>
        <p className="text-sm opacity-70 -mt-2">
          Plan your kiln loads, check capacity, and avoid half-empty or
          overloaded firings.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* LEFT COLUMN: kiln + capacity */}
        <div className="mudo-card p-4 space-y-4">
          <h2 className="mudo-section-title">Kiln</h2>

          <div>
            <label className="text-xs opacity-80">Select kiln</label>
            <select
              value={selectedKilnId || ""}
              onChange={(e) => setSelectedKilnId(e.target.value || null)}
              className="w-full rounded-xl bg-[#050608] border border-white/10 p-2 mt-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
            >
              <option
                value=""
                style={{ backgroundColor: "#020617", color: "#e5e7eb" }}
              >
                Choose a kiln…
              </option>
              {kilns.map((k) => (
                <option
                  key={k.id}
                  value={k.id}
                  style={{ backgroundColor: "#020617", color: "#e5e7eb" }}
                >
                  {k.name} {k.capacityLitres ? `(${k.capacityLitres} L)` : ""}
                </option>
              ))}
            </select>
          </div>

          {selectedKiln ? (
            <>
              <div className="text-sm space-y-1">
                <div>{selectedKiln.type || "Kiln"}</div>
                <div className="opacity-80">
                  Capacity:{" "}
                  {selectedKiln.usableSpaceUnits
                    ? `${selectedKiln.usableSpaceUnits} units`
                    : "Set usable space units in Kiln settings"}
                </div>
              </div>

              <div className="mt-3">
                <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${percentFull}%`,
                      backgroundColor: capacityBg,
                    }}
                  />
                </div>
                <p className="text-xs mt-1 opacity-70">{percentFull}% full</p>
              </div>

              <div className="text-xs opacity-80 space-y-1">
                <div>Total load: {loadInfo.totalSpaceUnits ?? 0} units</div>
                {loadInfo.capacity && (
                  <div>
                    Capacity: {loadInfo.capacity} units (
                    {loadInfo.loadRatio
                      ? Math.round(loadInfo.loadRatio * 100)
                      : 0}
                    %)
                  </div>
                )}
              </div>

              <div className="space-y-1 text-xs">
                {loadInfo.suggestions?.map((s, idx) => {
                  let color = "text-slate-300";
                  if (s.type === "overCapacity") color = "text-red-300";
                  else if (s.type === "ideal") color = "text-green-300";
                  else if (
                    s.type === "underFilled" ||
                    s.type === "slightlyUnderFilled"
                  )
                    color = "text-amber-300";

                  return (
                    <div key={idx} className={color}>
                      • {s.message}
                    </div>
                  );
                })}
              </div>

              <div className="border border-dashed border-cyan-500/40 rounded-xl px-3 py-2 text-[11px] space-y-1 mt-2">
                <div className="flex items-center justify-between">
                  <span className="uppercase tracking-wide opacity-70">Mudo AI</span>
                  <button
                    type="button"
                    onClick={handleAskKilnAi}
                    disabled={!selectedKiln || loadItems.length === 0 || aiLoading || pricingSettings?.enableKilnAi === false}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aiLoading ? "Thinking…" : "Review this firing"}
                  </button>
                </div>
                {aiError && (
                  <p className="text-[11px] text-red-300 mt-1">{aiError}</p>
                )}
                {aiCoach && !aiError && (
                  <div className="mt-1 space-y-1">
                    <div className="font-medium opacity-80">{aiCoach.title}</div>
                    <p className="opacity-80">{aiCoach.summary}</p>
                    {Array.isArray(aiCoach.bullets) && aiCoach.bullets.length > 0 && (
                      <ul className="list-disc list-inside opacity-80 space-y-0.5">
                        {aiCoach.bullets.map((b, idx) => (
                          <li key={idx}>{b}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSplitFiring}
                  disabled={!selectedKiln || loadItems.length === 0}
                  className="text-xs px-3 py-2 rounded-full border border-white/10 bg-black/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Split firing (beta)
                </button>

                <button
                  onClick={handleSuggestTopUp}
                  disabled={!selectedKiln || loadItems.length === 0}
                  className="text-xs px-3 py-2 rounded-full border border-white/10 bg-black/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suggest top-up
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm opacity-70">
              Choose a kiln to start planning a firing. You can add kilns in the
              Kilns settings panel.
            </p>
          )}
        </div>

        {/* MIDDLE COLUMN: current load */}
        <div className="mudo-card p-4 space-y-4">
          <h2 className="mudo-section-title">Current Firing Load</h2>
          <p className="text-xs opacity-70">
            Batches included in this firing. Remove items to free capacity.
          </p>

          {loadItems.length === 0 ? (
            <p className="text-sm opacity-70">
              No batches added yet. Select from the list on the right.
            </p>
          ) : (
            <div className="space-y-2">
              {loadItems.map((item) => (
                <div
                  key={item.id}
                  className="border border-white/10 rounded-xl p-3 flex justify-between items-center"
                >
                  <div className="text-sm">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs opacity-70">
                      {item.quantity} pcs • {item.spaceUnitsPerPiece} units/pc •{" "}
                      {item.totalSpaceUnits} units
                    </div>
                  </div>
                  <button
                    onClick={() => toggleBatch(item.id)}
                    className="text-xs px-3 py-1 rounded-full border border-white/10 bg-black/40"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: available batches */}
        <div className="mudo-card p-4 space-y-4">
          <h2 className="mudo-section-title">Available Batches</h2>
          <p className="text-xs opacity-70">
            Ready or near-ready batches you can include in this firing.
          </p>

          {availableBatches.length === 0 ? (
            <p className="text-sm opacity-70">No batches available yet.</p>
          ) : (
            <div className="space-y-2">
              {availableBatches.map((batch) => {
                const selected = loadBatchIds.includes(batch.id);
                return (
                  <div
                    key={batch.id}
                    className={`border border-white/10 rounded-xl p-3 flex justify-between items-center ${
                      selected ? "bg-cyan-500/10" : ""
                    }`}
                  >
                    <div className="text-sm">
                      <div className="font-medium">{batch.name}</div>
                      <div className="text-xs opacity-70">
                        {batch.quantity} pcs • {batch.spaceUnitsPerPiece}{" "}
                        units/pc • {batch.totalSpaceUnits} units
                      </div>
                    </div>
                    <button
                      onClick={() => toggleBatch(batch.id)}
                      className="text-xs px-3 py-1 rounded-full border border-white/10 bg-black/40"
                    >
                      {selected ? "Remove" : "Add"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
